
























































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch
} from '~/utils/nuxt3-migration'
import {
  CLASSIFIED_VIEW_NS,
  ClassifiedViewState
} from '~/store/modules/shared/classifieds/view/state'
import { useNamespacedStore } from '~/compositions/store'
import { defineComponentTranslations } from '~/utils/i18n'
import { formatPrice } from '~/utils/number'
import { ciCart } from '~/icons/source/solid/cart'
import { ciShipping } from '~/icons/source/regular/shipping'
import { ciEshop } from '~/icons/source/regular/e-shop'
import { ciEdit } from '~/icons/source/solid/edit'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import { useDeps } from '~/compositions/dependency-container'
import MarketplaceCartService from '~/services/marketplace/MarketplaceCartService'
import { useSnackbar } from '~/compositions/snackbar'
import { useI18n } from '~/compositions/i18n'
import { useLoginModal } from '~/compositions/login-modal'
import RouteGuardService from '~/services/RouteGuardService'
import { ciMinus } from '~/icons/source/light/minus'
import { ciPlus } from '~/icons/source/light/plus'
import { ciTimes } from '~/icons/source/solid/times'
import { ciCheck } from '~/icons/source/solid/check'
import { ClassifiedMarketplaceInfo } from '~/models/classified/view'
import { debounce } from '~/utils/function'
import { marketplaceCartMaxQuantity } from '~/constants/marketplace'
import { FacebookPixelService } from '~/services/telemetry/FacebookPixelService'
import AnalyticsService from '~/services/AnalyticsService'
import {
  DECREMENT_MARKETPLACE_BUYER_CART_ITEMS_COUNT,
  INCREMENT_MARKETPLACE_BUYER_CART_ITEMS_COUNT
} from '~/store/modules/shared/user/mutation-types'
import CImg from '~/components/shared/configurable/image/CImg.vue'
import MarketplaceSectionCartButtonPopup from '~/components/car/classifieds/view/partials/marketplace/MarketplaceSectionCartButtonPopup.vue'

export default defineComponent({
  components: { MarketplaceSectionCartButtonPopup, CImg },
  props: {
    marketplace: {
      type: Object as PropType<ClassifiedMarketplaceInfo>,
      required: true
    }
  },
  setup(props, { emit }) {
    const [
      cartService,
      routeGuardService,
      facebookPixelService,
      analyticsService
    ] = useDeps(
      MarketplaceCartService,
      RouteGuardService,
      FacebookPixelService,
      AnalyticsService
    )
    const {
      state: classifiedViewState,
      getters: classifiedViewGetters
    } = useNamespacedStore<ClassifiedViewState>(CLASSIFIED_VIEW_NS)
    const { state: userState, commit: userCommit } = useNamespacedStore<
      UserState
    >(USER_NS)
    const snackbar = useSnackbar()
    const { t } = useI18n()
    const { showModal: showLoginModal } = useLoginModal({
      onSuccess: onAddToCartButtonClick
    })

    const cartPopupIsShown = ref(false)
    const loading = ref(false)
    const productQuantity = ref(props.marketplace.cart.productQuantity)

    watch(productQuantity, (_newQuantity, oldQuantity) => {
      if (oldQuantity === 0) {
        cartPopupIsShown.value = true
        userCommit(INCREMENT_MARKETPLACE_BUYER_CART_ITEMS_COUNT)
      }
    })

    const classified = computed(() => classifiedViewState.classified)

    const currentUser = computed(() => classifiedViewState.currentUser)

    const firstThumb = computed(
      () => classifiedViewGetters('processedPhotos')[0]?.thumb
    )

    const currentUserId = computed(() => userState.id)

    const userOwnsProduct = computed(
      () => props.marketplace.product.seller.userId === currentUserId.value
    )

    const isIncreaseQuantityButtonDisabled = computed(
      () =>
        productQuantity.value === marketplaceCartMaxQuantity || loading.value
    )

    const userHasProductInCart = computed(() => productQuantity.value > 0)

    const isCartButtonDisabled = computed(
      () =>
        props.marketplace.product.availability === 'out_of_stock' &&
        props.marketplace.product.stockCount === 0
    )

    async function onAddToCartButtonClick() {
      if (!classified.value) {
        return
      }

      if (!routeGuardService.userIsLoggedIn()) {
        showLoginModal()
        return
      }

      await addItemToCart(1)

      facebookPixelService.sendEvent('AddToCart', {
        content_ids: [classified.value.id],
        content_type: 'product',
        value: props.marketplace.product.price,
        currency: 'EUR'
      })

      analyticsService.recordEvent({
        namespace: 'add_to_cart',
        customData: {
          currency: 'EUR',
          value: props.marketplace.product.price,
          items: [
            {
              item_id: classified.value.id,
              item_name: classified.value.title,
              item_category: classified.value.category,
              quantity: 1
            }
          ]
        }
      })

      productQuantity.value = 1
    }

    async function onReduceCartItemButtonClick() {
      if (productQuantity.value === 1) {
        productQuantity.value = 0
        debouncedAddItemToCart.cancel()
        await removeItemFromCart()

        return
      }

      productQuantity.value -= 1
      debouncedAddItemToCart(productQuantity.value)
    }

    function onIncreaseCartItemButtonClick() {
      productQuantity.value += 1
      debouncedAddItemToCart(productQuantity.value)
    }

    async function removeItemFromCart() {
      loading.value = true

      try {
        await cartService.removeCartItem(props.marketplace.product.id)

        userCommit(DECREMENT_MARKETPLACE_BUYER_CART_ITEMS_COUNT)
        cartPopupIsShown.value = false
        snackbar.success(t('item_removed_from_cart'))
        emit('remove-from-cart')
      } catch (err) {
        snackbar.error(err.response?.data.error || t('something went wrong'))
      } finally {
        loading.value = false
      }
    }

    async function addItemToCart(quantity: number) {
      loading.value = true

      try {
        await cartService.addCartItem({
          productId: props.marketplace.product.id,
          quantity
        })

        emit('add-to-cart')
      } catch (err) {
        snackbar.error(err.response?.data.error || t('something went wrong'))
      } finally {
        loading.value = false
      }
    }

    function onPopupClose() {
      cartPopupIsShown.value = false
    }

    const debouncedAddItemToCart = debounce(addItemToCart, 500)

    return {
      ciTimes,
      ciCart,
      ciShipping,
      ciEshop,
      ciEdit,
      ciPlus,
      ciMinus,
      ciCheck,
      classified,
      currentUser,
      loading,
      productQuantity,
      userOwnsProduct,
      currentUserId,
      userHasProductInCart,
      isIncreaseQuantityButtonDisabled,
      cartPopupIsShown,
      firstThumb,
      isCartButtonDisabled,
      formatPrice,
      onAddToCartButtonClick,
      onReduceCartItemButtonClick,
      onIncreaseCartItemButtonClick,
      onPopupClose
    }
  },
  i18n: defineComponentTranslations({
    add_to_cart: {
      en: 'Add to cart',
      el: 'Προσθήκη στο καλάθι'
    },
    sold_by: {
      en: 'Sold by',
      el: 'Πωλείται από'
    },
    edit_product: {
      en: 'Edit product',
      el: 'Επεξεργασία προϊόντος'
    },
    ability_to_pickup_from_store: {
      en: 'Ability to pickup from store',
      el: 'Δυνατότητα παραλαβής από κατάστημα'
    }
  })
})
